import { ReactElement, useState } from 'react';

import { RadioGroup as HeadlessRadioGroup } from '@headlessui/react';
import styled, { CSSObject } from 'styled-components';

import { FormError } from '@components/form-error';
import { FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { VisuallyHidden } from '@components/visually-hidden';
import { useEvent } from '@hooks/use-event';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { AnimatedRadio } from './animated-radio';

type RadioValue = string | number;

type RadioGroupPropsBase = {
  label: string;
  description?: string;
  isDisabled?: boolean;
  hideLabel?: boolean;
  defaultValue?: RadioValue;
  onChange?(newValue: RadioValue): void;
  value?: RadioValue;
  error?: string;
};

export type RadioGroupPropsChildren = RadioGroupPropsBase & {
  children: Array<ReactElement<RadioGroupOptionProps>>;
  options?: never;
};

export type RadioGroupPropsOptions = RadioGroupPropsBase & {
  children?: never;
  options: Array<RadioGroupOptionProps>;
};

export type RadioGroupProps = RadioGroupPropsChildren | RadioGroupPropsOptions;

export const RadioGroup = ({
  children,
  defaultValue,
  hideLabel,
  label,
  description,
  onChange,
  options,
  error,
  value,
  isDisabled = false,
}: RadioGroupProps) => {
  const isControlled = value !== undefined;

  const [localSelected, setLocalSelected] = useState<RadioValue>(defaultValue ?? '');

  const handleChange = useEvent((value: RadioValue) => {
    setLocalSelected(value);
    onChange?.(value);
  });

  const selectedValue = isControlled ? value : localSelected;

  const labelElement = <HeadlessRadioGroup.Label as={FormLabel} text={label} />;

  return (
    <HeadlessRadioGroup
      value={selectedValue}
      onChange={handleChange}
      disabled={isDisabled}
      defaultValue={defaultValue}
    >
      <Stack space={1} orientation="vertical">
        {hideLabel ? <VisuallyHidden>{labelElement}</VisuallyHidden> : labelElement}
        {description && <Text font="regular14" text={description} />}
        <div>
          {children ??
            options?.map((option) => <RadioGroupOption key={option.value} {...option} />)}
        </div>
        <FormError text={error} />
      </Stack>
    </HeadlessRadioGroup>
  );
};

export type RadioGroupOptionProps = {
  value: string | number;
  label: string;
  isDisabled?: boolean;
  icon?: ReactElement<IconProps>;
  disabledTag?: ReactElement;
  helpText?: string;
  textStyles?: CSSObject;
};

const RadioGroupOption = (props: RadioGroupOptionProps) => {
  const { value, label, isDisabled = false, icon, disabledTag, helpText, textStyles = {} } = props;
  return (
    <HeadlessRadioGroup.Option as={Option} value={value} disabled={isDisabled}>
      {({ checked }) => (
        <>
          <AnimatedRadio checked={checked} />
          {icon && <icon.type {...icon.props} size={16} />}
          <Text font="regular14" text={label} style={textStyles} />
          {helpText && <Text font="regular14" color="textHelper" text={helpText} />}
          <ExtraTag data-full-opacity="true">{disabledTag}</ExtraTag>
        </>
      )}
    </HeadlessRadioGroup.Option>
  );
};

RadioGroup.Option = RadioGroupOption;

const Option = styled(Stack).attrs({
  justify: 'flex-start',
  orientation: 'horizontal',
  space: 1,
  align: 'center',
})({
  outline: 'none',
  minHeight: theme.base.height,
  borderRadius: theme.form.border.radius,
  padding: '0.5rem 0',
  color: theme.color.interactiveElementNeutral,

  svg: {
    borderRadius: '50%',
    '--shadow-focus-color': theme.color.elementBlue100,
    '--shadow-focus-gap': '0px',
    '--shadow-focus-width': '0.15rem',
  },

  '&:focus': {
    outline: 'none',
  },

  '&[data-headlessui-state~=checked]': {
    color: theme.color.interactiveElementSelected,
  },

  '&[data-headlessui-state~=disabled]': {
    '& > :not([data-full-opacity="true"])': {
      opacity: 0.5,
    },
  },

  '&:not([data-headlessui-state~=disabled])': {
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: theme.color.uiTrans5,
    },
  },

  '&:focus-visible': {
    svg: {
      boxShadow: theme.shadow.focus,
    },

    '&[data-headlessui-state~=checked]': {
      color: theme.form.value.colorFocus,
    },
  },
});

const ExtraTag = styled.div({
  marginLeft: pxToRem(4),
});
